import React from 'react';
import {
  LineChart,
  AreaChart,
  BarChart,
  PieChart,
  RadialChart,
  ScatterChart,
  violetPalette,
  vibrantPalette,
} from '@strivelabs/ui-blocks';

// Mock data for LineChart - Auto-styled with violet theme
const lineChartData = [
  { month: 'Jan', revenue: 4000, expenses: 2400 },
  { month: 'Feb', revenue: 3000, expenses: 1398 },
  { month: 'Mar', revenue: 2000, expenses: 9800 },
  { month: 'Apr', revenue: 2780, expenses: 3908 },
  { month: 'May', revenue: 1890, expenses: 4800 },
  { month: 'Jun', revenue: 2390, expenses: 3800 },
];

// Mock data for AreaChart - Auto-styled with violet theme
const areaChartData = [
  { month: 'Jan', desktop: 186, mobile: 80 },
  { month: 'Feb', desktop: 305, mobile: 200 },
  { month: 'Mar', desktop: 237, mobile: 120 },
  { month: 'Apr', desktop: 73, mobile: 190 },
  { month: 'May', desktop: 209, mobile: 130 },
  { month: 'Jun', desktop: 214, mobile: 140 },
];

// Mock data for BarChart - Auto-styled with violet theme
const barChartData = [
  { month: 'Jan', sales: 4000, profit: 2400 },
  { month: 'Feb', sales: 3000, profit: 1398 },
  { month: 'Mar', sales: 2000, profit: 9800 },
  { month: 'Apr', sales: 2780, profit: 3908 },
  { month: 'May', sales: 1890, profit: 4800 },
  { month: 'Jun', sales: 2390, profit: 3800 },
  { month: 'Jul', sales: 4000, profit: 2400 },
  { month: 'Aug', sales: 3000, profit: 1398 },
  { month: 'Sep', sales: 2000, profit: 9800 },
];

// Mock data for PieChart - Auto-styled with violet theme
const pieChartData = [
  { name: 'Chrome', value: 400 },
  { name: 'Safari', value: 300 },
  { name: 'Firefox', value: 200 },
  { name: 'Edge', value: 100 },
];

// Mock data for RadialChart - Auto-styled with violet theme
const radialChartData = [
  { name: 'A', value: 80 },
  { name: 'B', value: 65 },
  { name: 'C', value: 45 },
];

// Mock data for ScatterChart - Auto-styled with violet theme
const scatterData1 = [
  { x: 100, y: 200, z: 200 },
  { x: 120, y: 100, z: 260 },
  { x: 170, y: 300, z: 400 },
  { x: 140, y: 250, z: 280 },
  { x: 150, y: 400, z: 500 },
  { x: 110, y: 280, z: 200 },
];

const scatterData2 = [
  { x: 200, y: 260, z: 240 },
  { x: 240, y: 290, z: 220 },
  { x: 190, y: 290, z: 250 },
  { x: 198, y: 250, z: 210 },
  { x: 180, y: 280, z: 260 },
  { x: 210, y: 220, z: 230 },
];

// Mock data with LONG labels to test truncation in small viewports
const longLabelLineChartData = [
  { campaign: 'Brand Awareness Campaign Q4 2024', clicks: 4500, impressions: 25000 },
  { campaign: 'Product Launch - Winter Collection 2024', clicks: 3200, impressions: 18000 },
  { campaign: 'Retargeting - Holiday Shopping Season', clicks: 5800, impressions: 32000 },
  { campaign: 'Search Campaign - Competitive Keywords', clicks: 4100, impressions: 21000 },
  { campaign: 'Display Network - Lifestyle Targeting', clicks: 2900, impressions: 16000 },
  { campaign: 'Video Campaign - YouTube Pre-Roll Ads', clicks: 3700, impressions: 19500 },
  { campaign: 'Social Media - Instagram Stories Q4', clicks: 4300, impressions: 23000 },
  { campaign: 'Email Remarketing - Abandoned Cart', clicks: 3500, impressions: 17500 },
  { campaign: 'Partnership Campaign - Influencer Collab', clicks: 4800, impressions: 26500 },
  { campaign: 'Local Market Campaign - Regional Focus', clicks: 3100, impressions: 15800 },
  { campaign: 'Mobile App Install Campaign - iOS/Android', clicks: 5200, impressions: 29000 },
  { campaign: 'Seasonal Promotion - Black Friday 2024', clicks: 6100, impressions: 35000 },
];

const longLabelBarChartData = [
  { product: 'Premium Wireless Headphones Pro Max', sales: 4200, revenue: 8400 },
  { product: 'Smart Watch Series 5 - Fitness Edition', sales: 3800, revenue: 7600 },
  { product: 'Laptop Stand Adjustable Aluminum Alloy', sales: 2100, revenue: 4200 },
  { product: 'USB-C Hub 7-in-1 MultiPort Adapter', sales: 5300, revenue: 10600 },
  { product: 'Mechanical Keyboard RGB Backlit Gaming', sales: 3500, revenue: 7000 },
  { product: 'External SSD 2TB Portable Drive NVMe', sales: 4700, revenue: 9400 },
  { product: 'Webcam 4K Ultra HD with Ring Light', sales: 3200, revenue: 6400 },
  { product: 'Wireless Mouse Ergonomic Vertical Design', sales: 2800, revenue: 5600 },
  { product: 'Monitor 27" 4K UHD IPS Panel 144Hz', sales: 4100, revenue: 8200 },
  { product: 'Desk Lamp LED Adjustable Brightness', sales: 1900, revenue: 3800 },
];

// Weekly performance data matching Figma mock design for testing purpose
const weeklyPerformanceData = [
  { week: 'Week 3', value: 19 },
  { week: 'Week 2', value: 8 },
  { week: 'Last Week', value: 8 },
  { week: 'This Week', value: 1 },
];

export const ChartsDemo: React.FC = () => {
  return (
    <div className="container mx-auto p-8 space-y-8">
      <h1 className="text-3xl font-bold mb-8">Charts Demo - Violet Theme</h1>

      {/* NEW: Violet Theme Showcase - Matching Figma Mock */}
      <div className="border-2 border-violet-500 rounded-lg p-6 bg-violet-50">
        <h2 className="text-2xl font-bold mb-4 text-violet-900">
          New Violet Theme - Matching Figma Design
        </h2>
        <p className="text-sm text-violet-700 mb-4">
          Charts now use the new violet color palette by default. Click the expand button (top-right) to view in fullscreen.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Bar Chart matching Figma design */}
          <div>
            <BarChart
              data={weeklyPerformanceData}
              xAxisKey="week"
              title="Weekly Performance"
              description="Click expand icon to view fullscreen"
              height={280}
              showLegend={false}
            />
          </div>

          {/* Line Chart with violet theme */}
          <div>
            <LineChart
              data={lineChartData}
              title="Revenue Trend"
              description="Monthly revenue with violet theme"
              xAxisKey="month"
              height={280}
              tooltipFormatter={(value) => `$${value.toLocaleString()}`}
            />
          </div>
        </div>
      </div>

      {/* Chat Panel Width Test Section */}
      <div className="border-2 border-orange-500 rounded-lg p-6 bg-orange-50">
        <h2 className="text-2xl font-bold mb-4 text-orange-900">
          Chat Panel Width Test (5/12 Grid Columns)
        </h2>
        <p className="text-sm text-orange-700 mb-4">
          This section mimics the chat panel width in the Google Ads agent layout.
          Testing charts with long labels in constrained space.
        </p>

        {/* Simulate the 5/12 column width */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-5 border-2 border-orange-300 rounded-lg bg-white">
            {/* LineChart with long labels */}
            <div className="p-4">
              <LineChart
                data={longLabelLineChartData}
                title="Campaign Performance"
                description="Testing label truncation with long campaign names"
                xAxisKey="campaign"
                height={300}
                tooltipFormatter={(value) => `${value.toLocaleString()}`}
              />
            </div>

            {/* BarChart with long labels */}
            <div className="p-4 border-t">
              <BarChart
                data={longLabelBarChartData}
                xAxisKey="product"
                title="Product Sales"
                height={300}
              />
            </div>
          </div>

          {/* Reference: Full width version for comparison */}
          <div className="col-span-7 border-2 border-blue-300 rounded-lg bg-blue-50 p-4">
            <h3 className="text-sm font-semibold mb-2 text-blue-800">Full Width Reference (7/12 columns)</h3>
            <p className="text-xs text-blue-600 mb-3">
              Same charts in larger space for comparison
            </p>
            <div className="space-y-4">
              <LineChart
                data={longLabelLineChartData}
                title="Campaign Performance (Full Width)"
                xAxisKey="campaign"
                height={250}
                tooltipFormatter={(value) => `${value.toLocaleString()}`}
              />
              <BarChart
                data={longLabelBarChartData}
                xAxisKey="product"
                title="Product Sales (Full Width)"
                height={250}
              />
            </div>
          </div>
        </div>
      </div>

      {/* LineChart Demo - Violet Theme */}
      <LineChart
        data={lineChartData}
        title="Revenue vs Expenses"
        description="Monthly comparison - auto-styled with violet theme"
        xAxisKey="month"
        tooltipFormatter={(value) => `$${value.toLocaleString()}`}
      />

      {/* AreaChart Demo - Violet Theme */}
      <AreaChart
        data={areaChartData}
        title="Desktop vs Mobile Users"
        description="User distribution - auto-styled with violet theme"
        xAxisKey="month"
        tooltipFormatter={(value) => `${value} users`}
      />

      {/* BarChart Demo - Violet Theme */}
      <BarChart
        data={barChartData}
        xAxisKey="month"
        title="Sales and Profit"
        description="Monthly sales data with violet palette"
      />

      {/* PieChart Demo - Violet Theme */}
      <PieChart
        data={pieChartData}
        title="Browser Market Share"
        description="Distribution of users by browser - violet theme"
        dataKey="value"
        nameKey="name"
        tooltipFormatter={(value) => `${value} users`}
      />

      {/* PieChart with Donut variant */}
      <PieChart
        data={pieChartData}
        title="Browser Market Share (Donut)"
        description="Donut variant with violet theme"
        dataKey="value"
        nameKey="name"
        innerRadius={60}
        tooltipFormatter={(value) => `${value} users`}
      />

      {/* RadialChart Demo - Violet Theme */}
      <RadialChart
        data={radialChartData}
        title="Performance Metrics"
        description="Category performance comparison - violet theme"
        dataKey="value"
        tooltipFormatter={(value) => `${value}%`}
      />

      {/* ScatterChart Demo - Violet Theme */}
      <ScatterChart
        data={[]}
        title="Data Distribution"
        description="Scatter plot showing data distribution - violet theme"
        xAxisKey="x"
        yAxisKey="y"
        scatters={[
          { name: 'Group A', data: scatterData1 },
          { name: 'Group B', data: scatterData2 },
        ]}
        xAxisLabel="X Axis"
        yAxisLabel="Y Axis"
      />

      {/* BarChart with Custom Violet Colors */}
      <BarChart
        data={barChartData}
        xAxisKey="month"
        title="Sales and Profit (Custom Violet Colors)"
        description="Using violetPalette explicitly"
        dataKeys={[
          { key: 'sales', name: 'Sales', color: violetPalette[0] },
          { key: 'profit', name: 'Profit', color: violetPalette[2] },
        ]}
      />

      {/* BarChart with Legacy Vibrant Colors (for comparison) */}
      <BarChart
        data={barChartData}
        xAxisKey="month"
        title="Sales and Profit (Legacy Vibrant Colors)"
        description="Using vibrantPalette for comparison"
        dataKeys={[
          { key: 'sales', name: 'Sales', color: vibrantPalette[0] },
          { key: 'profit', name: 'Profit', color: vibrantPalette[1] },
        ]}
      />

      {/* Advanced: Stacked AreaChart with violet config */}
      <AreaChart
        data={areaChartData}
        title="Stacked Desktop vs Mobile Users"
        description="Stacked view with violet palette"
        xAxisKey="month"
        areas={[
          { dataKey: 'desktop', fill: violetPalette[0], stackId: '1' },
          { dataKey: 'mobile', fill: violetPalette[2], stackId: '1' },
        ]}
        tooltipFormatter={(value) => `${value} users`}
      />

      {/* Non-expandable Chart Example */}
      <BarChart
        data={weeklyPerformanceData}
        xAxisKey="week"
        title="Non-Expandable Chart"
        description="This chart has expandable=false"
        height={250}
        showLegend={false}
        expandable={false}
      />
    </div>
  );
};
